import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Observable, tap } from 'rxjs';
import { BaseForm } from '../../forms/forms/base.form';
import { DashboardProductForm } from '../../forms/forms/dashboard-product.form';
import { ProductActionType } from '../../shared/enums/product-action-type.enum';
import { DashboardForms } from '../../shared/models/dashboard-forms.model';
import { ProductWithBarcode } from '../../shared/models/product-with-barcode.model';
import { SortOrder } from '../api/api';
import {
  Barcode,
  CreateBarcodeRequest,
  Product,
  ProductQuantityUnit,
  Purchase,
  PurchaseProductAction,
  ShoppingSession,
  Success,
} from '../api/api.entities';
import { BarcodesApi } from '../api/barcodes/barcodes-api.service';
import { ProductAction } from '../api/products/entities/product-action.entity';
import { ProductsApi } from '../api/products/products-api.service';
import { PurchasesApi } from '../api/purchases/purchases-api.service';
import { ShoppingSessionsApi } from '../api/shopping-sessions/shopping-session-api.service';
import { StockItemsApi } from '../api/stock-items/stock-items-api.service';
import { SelectedProductService } from './selected-product.service';
import { StaticDataService } from './static-data.service';

@Injectable({
  providedIn: 'root',
})
export class DashboardService extends SelectedProductService {
  forms = new DashboardForms();
  productActionType = ProductActionType.Consume;

  constructor(
    barcodesApi: BarcodesApi,
    private matSnackBar: MatSnackBar,
    productsApi: ProductsApi,
    private purchasesApi: PurchasesApi,
    private shoppingSessionsApi: ShoppingSessionsApi,
    private staticDataService: StaticDataService,
    private stockItemsApi: StockItemsApi
  ) {
    super(barcodesApi, productsApi);
    this.initialiseForms();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get dependingForms(): BaseForm<any>[] {
    return [this.forms.shopping.item];
  }

  get productForm(): DashboardProductForm {
    return this.forms.product;
  }

  get quantityUnit(): ProductQuantityUnit {
    if (this.product) {
      return this.productActionType === ProductActionType.Purchase
        ? this.product.quantityUnitPurchase
        : this.product.quantityUnitStock;
    } else throw new Error('No product selected');
  }

  override clearForms(clearProduct: boolean = false): void {
    super.clearForms(clearProduct);

    this.forms.shopping.item.product = undefined;

    for (const quickActionForm of Object.values(this.forms.quickAction)) {
      quickActionForm.clear();
    }
  }

  createShoppingSession(): Observable<ShoppingSession> {
    if (!this.forms.shopping.session.isValid) {
      throw new Error('Shopping session form is not valid');
    }

    const body = this.forms.shopping.session.data;

    return this.shoppingSessionsApi.create(body).pipe(
      tap((shoppingSession: ShoppingSession) => {
        this.shoppingSessionsApi
          .findAllCompact({ sort: [{ column: 'date', order: SortOrder.desc }] })
          .subscribe(shoppingSessions => {
            this.forms.shopping.session.options.shoppingSessions = shoppingSessions;

            this.forms.shopping.session.controls.existingShoppingSession.setValue(
              shoppingSessions.find(session => session.id === shoppingSession.id) || null
            );
          });
      })
    );
  }

  override loadSelectedProduct(product: ProductWithBarcode): void {
    this.forms.shopping.item.storeId = this.forms.shopping.session.store?.id;
    this.forms.shopping.item.clear();
    this.forms.shopping.item.product = product;

    for (const quickActionForm of Object.values(this.forms.quickAction)) {
      quickActionForm.product = product;
      quickActionForm.controls.stockItem.setValue(null);
    }

    super.loadSelectedProduct(product);
  }

  submitQuickAction(productAction: ProductAction): Observable<Product> {
    this.disableForms();
    this.state.isSubmitting = true;

    return new Observable(subscriber => {
      this.productsApi
        .executeProductAction(this.product!.id, this.productActionType, productAction)
        .subscribe((product: Product) => {
          subscriber.next(product);
          subscriber.complete();
          this.postProcessProductAction(productAction);
        });
    });
  }

  registerPurchase(): Observable<Purchase[]> {
    this.disableForms();
    this.state.isSubmitting = true;

    return new Observable(subscriber => {
      const productAction: PurchaseProductAction = this.forms.shopping.item.getProductAction();
      productAction.shoppingSessionId = this.forms.shopping.session.existingShoppingSession!.id;

      this.productsApi
        .executeProductPurchase(this.product!.id, productAction)
        .subscribe((recentPurchases: Purchase[]) => {
          subscriber.next(recentPurchases);
          this.postProcessProductAction(productAction);
        });
    });
  }

  undoPurchase(purchaseId: number): Observable<boolean> {
    this.disableForms();
    this.state.isSubmitting = true;

    return new Observable(subscriber => {
      this.purchasesApi.deleteOne(purchaseId).subscribe((success: Success) => {
        subscriber.next(success.succeeded);
        this.matSnackBar.open(success.message, 'Dismiss', { duration: 5000 });
        this.findProductByName(this.product!.id);
      });
    });
  }

  undoRecentAction(stockItemId: number, type: ProductActionType): Observable<boolean> {
    this.disableForms();
    this.state.isSubmitting = true;

    return new Observable(subscriber => {
      this.stockItemsApi.undoRecentAction(stockItemId, type).subscribe((success: Success) => {
        subscriber.next(success.succeeded);
        this.matSnackBar.open(success.message, 'Dismiss', { duration: 5000 });
        this.findProductByName(this.product!.id);
      });
    });
  }

  private get barcodeData(): CreateBarcodeRequest {
    if (this.product) {
      return {
        amount: this.forms.shopping.item.amount,
        barcode: this.forms.product.barcode!,
        productId: this.product.id,
        quantityUnitId: this.quantityUnit.id,
      };
    } else throw new Error('No product selected');
  }

  private constructProductActionSnackbarMessage(success: boolean, { amount }: ProductAction): string {
    const quantityUnitString = amount === 1 ? this.quantityUnit.name : this.quantityUnit.namePlural;

    const message = `${this.productActionType.toLowerCase()} of ${amount} ${quantityUnitString} ${this.product?.name}`;

    return success ? `Succeeded ${message}` : `Failed to execute ${message}`;
  }

  private postProcessProductAction(productAction: ProductAction): void {
    let message = this.constructProductActionSnackbarMessage(true, productAction);

    if (this.forms.product.barcodeExists || !this.forms.product.barcode) {
      this.matSnackBar.open(message, 'Dismiss', { duration: 5000 });
      this.findProductByName(this.product!.id);
    } else {
      this.barcodesApi.create(this.barcodeData).subscribe((barcode: Barcode) => {
        message += ` and successfully linked ${barcode.barcode} to ${this.product?.name}`;
        this.matSnackBar.open(message, 'Dismiss', { duration: 5000 });
        this.findProductByName(this.product!.id);
      });
    }
  }

  override disableForms(): void {
    this.forms.product.disable();
    this.forms.quickAction.consume.disable();
    this.forms.quickAction.open.disable();
    this.forms.quickAction.transfer.disable();
    this.forms.shopping.item.disable();
  }

  override enableForms(): void {
    this.forms.product.enable();
    this.forms.quickAction.consume.enable();
    this.forms.quickAction.open.enable();
    this.forms.quickAction.transfer.enable();
    this.forms.shopping.item.enable();
  }

  private initialiseForms(): void {
    const shoppingSessionQuery = { sort: [{ column: 'date', order: SortOrder.desc }] };

    forkJoin({
      locations: this.staticDataService.getLocations(),
      shoppingSessions: this.shoppingSessionsApi.findAllCompact(shoppingSessionQuery),
      stores: this.staticDataService.getStores(),
    }).subscribe(({ locations, shoppingSessions, stores }) => {
      this.forms.quickAction.consume.options.locations = locations;
      this.forms.quickAction.open.options.locations = locations;
      this.forms.quickAction.transfer.options.locations = locations;
      this.forms.shopping.item.options.locations = locations;
      this.forms.shopping.session.options.stores = stores;
      this.forms.shopping.session.options.shoppingSessions = shoppingSessions;
    });
  }
}
